import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import * as Sentry from '@sentry/vue'

// Import our custom styles AFTER ElementPlus to override their defaults
import './assets/main.css'

import App from './App.vue'
import router from './router'
import { initializeContainer } from './container/container'
import { errorNotifier } from './utils/errorNotifier'
import { sentryConfig } from './config/sentry'
import { isChunkLoadFailure, reloadOnceForStaleChunk } from './utils/chunkLoadError'
import { shouldShowErrorPageForUnhandledReason } from './utils/errorClassifier'
import { goToErrorPageFromUnknown } from './utils/errorHandler'

// Same path as router `error` lazy import — prefetch chunk at startup so /error works
// without an extra JS fetch when the user goes offline after the app loaded.
void import('@/views/ErrorView.vue')

const app = createApp(App)

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

app.use(pinia)
app.use(router)
app.use(ElementPlus)

// Initialize Sentry Vue integration
if (sentryConfig.enabled) {
  Sentry.init({
    app,
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment,
    release: sentryConfig.release,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: sentryConfig.tracesSampleRate,
    // Session Replay
    replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
  })
}

// Global error handler for Vue
app.config.errorHandler = (err, instance, info) => {
  // Log to Sentry
  if (sentryConfig.enabled) {
    Sentry.captureException(err, {
      tags: {
        errorType: 'vue-error',
        componentInfo: info,
      },
      contexts: {
        vue: {
          componentName: instance?.$options?.name || 'Unknown',
        },
      },
    })
  }

  console.error('Vue error:', err, info)

  if (router.currentRoute.value.name !== 'error' && err instanceof Error) {
    goToErrorPageFromUnknown(err, 'replace')
    return
  }

  errorNotifier.error('An unexpected error occurred. Please try again.')
}

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadFailure(event.reason)) {
    event.preventDefault()
    reloadOnceForStaleChunk()
    return
  }

  if (shouldShowErrorPageForUnhandledReason(event.reason)) {
    event.preventDefault()
    goToErrorPageFromUnknown(event.reason, 'replace')
    if (sentryConfig.enabled) {
      Sentry.captureException(event.reason, {
        tags: { errorType: 'unhandled-rejection-app-error' },
      })
    }
    console.error('Unhandled promise rejection (error page):', event.reason)
    return
  }

  // Log to Sentry
  if (sentryConfig.enabled) {
    Sentry.captureException(event.reason, {
      tags: {
        errorType: 'unhandled-rejection',
      },
    })
  }

  // Show user-friendly notification
  errorNotifier.error('An unexpected error occurred. Please try again.')
  console.error('Unhandled promise rejection:', event.reason)
})

initializeContainer(app)

app.mount('#app')
